import { CartCalendarModalAdd } from './CartCalendarModalAdd';
import { CartCalendarModalDetails } from './CartCalendarModalDetails';
import { daysOfTheWeekAsNumbers, daysOfTheWeekP, generalHelpers, months } from '@monorepo/helpers';
import { typeCartAssignment, typeCartsDoc, typeCartPeriod, typeCartPoint } from '@monorepo/models';
import { useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';
import { useState, FC, Dispatch, SetStateAction, useMemo } from 'react';

type propsType = {
    cartsDoc: typeCartsDoc;
    deleteAssignment?: (assignment: typeCartAssignment) => void;
    hidePickupCartPoint?: boolean;
    month: number;
    point: typeCartPoint;
    readOnly?: boolean;
    setRefreshCounter: Dispatch<SetStateAction<number>>;
    year: number;
};

export const CartCalendarItem: FC<propsType> = ({
    cartsDoc, deleteAssignment, hidePickupCartPoint, month, point, readOnly, setRefreshCounter, year
}) => {
    const [newAssignment, setNewAssignment] = useState<typeCartAssignment | null>(null);
    const [selectedAssignment, setSelectedAssignment] = useState<typeCartAssignment | null>(null);
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();

    const enabledPeriods: typeCartPeriod[] = useMemo(() => {
        const periods = readOnly ? [] : [...point.enabledPeriods] as typeCartPeriod[];
        for (const assignment of cartsDoc.assignments.filter(a => a.month === month && a.year === year)) {
            if (assignment.pointId !== point.id) {
                continue;
            }
            const aPeriod: typeCartPeriod = {
                endHour: assignment.endHour ?? 0,
                endMinutes: assignment.endMinutes ?? 0,
                id: assignment.id,
                startHour: assignment.startHour ?? 0,
                startMinutes: assignment.startMinutes ?? 0
            };
            if (!periods.some(p => p.endHour === aPeriod.endHour && p.endMinutes === aPeriod.endMinutes && p.startHour === aPeriod.startHour && p.startMinutes === aPeriod.startMinutes)) {
                periods.push(aPeriod);
            }
        }
        periods.sort((p1, p2) => p1.startHour - p2.startHour || p1.startMinutes - p2.startMinutes || p1.endHour - p2.endHour || p1.endMinutes - p2.endMinutes);
        return periods;
    }, [cartsDoc.assignments, point.enabledPeriods, point.id, readOnly]);

    return (
        <div className={`calendar border rounded ${isDarkMode ? 'bg-dark' : ''} shadow-sm mb-5`}>
            {selectedAssignment &&
                <CartCalendarModalDetails
                    closeModal={() => setSelectedAssignment(null)}
                    deleteAssignment={deleteAssignment}
                    selectedAssignment={selectedAssignment}
                />
            }
            {newAssignment &&
                <CartCalendarModalAdd
                    cartsDoc={cartsDoc}
                    closeModal={() => setNewAssignment(null)}
                    newAssignment={newAssignment}
                    setRefreshCounter={setRefreshCounter}
                />
            }
            <h3 className={`calendar-point-title ${isMobile ? 'bg-general-blue text-white py-3' : ''} text-center mb-3 mt-2`}>
                {point.name} - {months[month]}
            </h3>
            {isMobile ?
                <ItemMobile
                    cartsDoc={cartsDoc}
                    enabledPeriods={enabledPeriods}
                    month={month}
                    point={point}
                    readOnly={readOnly}
                    setNewAssignment={setNewAssignment}
                    setSelectedAssignment={setSelectedAssignment}
                    year={year}
                />
                :
                <ItemDesktop
                    cartsDoc={cartsDoc}
                    enabledPeriods={enabledPeriods}
                    month={month}
                    point={point}
                    readOnly={readOnly}
                    setNewAssignment={setNewAssignment}
                    setSelectedAssignment={setSelectedAssignment}
                    year={year}
                />
            }
            {!hidePickupCartPoint &&
                <div>
                    <div className={'text-center mt-1 fw-bold'}>
                        <p className={'mb-1'}>
                            Punto de retiro: {point.pickupCartPoint}
                        </p>
                    </div>
                </div>
            }
        </div>
    )
}

type propsType1 = {
    cartsDoc: typeCartsDoc;
    enabledPeriods: typeCartPeriod[];
    month: number;
    point: typeCartPoint;
    readOnly: boolean | undefined;
    setNewAssignment: Dispatch<SetStateAction<typeCartAssignment | null>>;
    setSelectedAssignment: Dispatch<SetStateAction<typeCartAssignment | null>>;
    year: number;
};

const ItemDesktop: FC<propsType1> = (props) => {

    return (
        <div className={'calendar-scroll-wrapper'}>
            <div className={'calendar-grid-inner'}>
                <div className={'calendar-header d-flex border-bottom'}>
                    <div className={'calendar-header-time flex-fill p-1 flex-shrink-0'}></div>
                    {(() => {
                        const days = daysOfTheWeekP;
                        const daysLToD = [...days.slice(1), days[0]];
                        return daysLToD.map(day =>
                            <div key={day} className={'calendar-header-day flex-fill text-center fw-bold border-start flex-shrink-0'}>
                                {day}
                            </div>
                        );
                    })()}
                </div>
                <div>
                    {!props.enabledPeriods.length &&
                        <div className={'text-center border-top border-bottom py-2 my-3'}>
                            Sin asignaciones para este mes
                        </div>
                    }
                    {props.enabledPeriods.map(period =>
                        <div key={period.id} className={'calendar-row d-flex align-items-start border-bottom py-1'}>
                            <div className={'calendar-time flex-fill text-center fw-bold border-end flex-shrink-0'}>
                                <span>{generalHelpers.GetTimeFromPeriodObject(period)}</span>
                            </div>
                            {daysOfTheWeekAsNumbers.map(weekday => {
                                const assignment = props.cartsDoc.assignments.find(a =>
                                    a.pointId === props.point.id
                                    && a.year === props.year
                                    && a.month === props.month
                                    && a.weekday === weekday
                                    && a.startHour === period.startHour
                                    && a.startMinutes === period.startMinutes
                                    && a.endHour === period.endHour
                                    && a.endMinutes === period.endMinutes
                                );
                                return (
                                    <div key={weekday}
                                        className={'calendar-cell flex-fill text-center border-start pointer custom-card flex-shrink-0'}
                                        onClick={assignment ?
                                            () => props.setSelectedAssignment(assignment)
                                            :
                                            props.readOnly ? () => {} : () => {
                                                props.setNewAssignment({
                                                    address: props.point.address,
                                                    endHour: period.endHour,
                                                    endMinutes: period.endMinutes,
                                                    id: 0,
                                                    month: props.month,
                                                    name: props.point.name,
                                                    participants: [],
                                                    pickupCartPoint: props.point.pickupCartPoint,
                                                    pointId: props.point.id,
                                                    startHour: period.startHour,
                                                    startMinutes: period.startMinutes,
                                                    wasDeleted: false,
                                                    weekday,
                                                    year: props.year
                                                });
                                            }
                                        }
                                    >
                                        {assignment ?
                                            <div className={'calendar-assignment rounded'}>
                                                {assignment.participants?.map(p =>
                                                    <div key={p.userId} className={`${p.name} ${p.lastName}`.length > 12 ? 'marquee' : ''}>
                                                        {p.name} {p.lastName}
                                                    </div>
                                                )}
                                            </div>
                                            :
                                            <div className={'calendar-cell-empty'}></div>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const ItemMobile: FC<propsType1> = (props) => {
    const mobileSlots: { weekday: number; period: typeCartPeriod; assignment: typeCartAssignment | null }[] = useMemo(() => {
        const slots: { weekday: number; period: typeCartPeriod; assignment: typeCartAssignment | null }[] = [];
        for (const weekday of daysOfTheWeekAsNumbers) {
            for (const period of props.enabledPeriods) {
                const assignment = props.cartsDoc.assignments.find(a =>
                    a.pointId === props.point.id && a.year === props.year && a.month === props.month && a.weekday === weekday
                    && a.startHour === period.startHour && a.startMinutes === period.startMinutes
                    && a.endHour === period.endHour && a.endMinutes === period.endMinutes
                ) ?? null;
                slots.push({ weekday, period, assignment });
            }
        }
        return slots;
    }, [props.cartsDoc.assignments, props.enabledPeriods, props.month, props.point.id, props.year]);

    const slotsToShow = props.readOnly ? mobileSlots.filter(s => s.assignment !== null) : mobileSlots;
    const byDay = daysOfTheWeekAsNumbers.map(wd => ({
        weekday: wd,
        dayName: daysOfTheWeekP[wd],
        slots: slotsToShow.filter(s => s.weekday === wd)
    })).filter(d => d.slots.some(s => s.assignment !== null));

    if (!byDay.length) {
        return (
            <div className={'text-center border-top border-bottom py-2 my-3'}>
                Sin asignaciones para este mes
            </div>
        );
    }

    return (
        <div className={'d-flex justify-content-center'}>
            <div className={'calendar-mobile-didactic'}>
                {byDay.map(({ weekday, dayName, slots }) =>
                    <div key={weekday} className={'calendar-mobile-day-block'}>
                        <div className={'calendar-mobile-day-header bg-green text-white text-center'}>
                            {dayName}
                        </div>
                        <div className={'calendar-mobile-day-slots'}>
                            {slots.map(({ period, assignment }, idx) =>
                                <div key={`${weekday}-${period.id}-${idx}`}
                                    role={'button'}
                                    tabIndex={0}
                                    className={`calendar-mobile-slot ${assignment ? 'calendar-mobile-slot-assigned' : 'calendar-mobile-slot-empty'}`}
                                    onClick={assignment ?
                                        () => props.setSelectedAssignment(assignment)
                                        :
                                        props.readOnly ? undefined : () => props.setNewAssignment({
                                            address: props.point.address,
                                            endHour: period.endHour,
                                            endMinutes: period.endMinutes,
                                            id: 0,
                                            month: props.month,
                                            name: props.point.name,
                                            participants: [],
                                            pickupCartPoint: props.point.pickupCartPoint,
                                            pointId: props.point.id,
                                            startHour: period.startHour,
                                            startMinutes: period.startMinutes,
                                            wasDeleted: false,
                                            weekday,
                                            year: props.year
                                        })
                                    }
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault(); (e.currentTarget as HTMLDivElement).click();
                                        }
                                    }}
                                >
                                    <span className={'calendar-mobile-slot-time'}>
                                        {generalHelpers.GetTimeFromPeriodObject(period)}
                                    </span>
                                    <span className={'calendar-mobile-slot-names'}>
                                        {assignment?.participants?.length ?
                                            assignment.participants.map(p => `${p.name} ${p.lastName}`).join(' y ')
                                            :
                                            <span className={'text-muted'}>
                                                {props.readOnly ? '—' : 'Sin asignar (tocar para agregar)'}
                                            </span>
                                        }
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
